import React, { useEffect, useState, useMemo } from 'react'
import { InputField, SearchInput, SpinnerIcon } from '../../atoms'

import { useDashboardInfo, useAvailableWidgets, useDashboardWidgets } from '../../../api/queries/useDashboard'
import { useAppContext } from '../../../context/appContext'
import { useParams } from 'react-router'

import { DashboardEditStyled } from './styled'
import Grid from '@material-ui/core/Grid'
import { PageTitle } from '../../atoms'
import { ActionDialog } from '../../molecules'
import { Snackbar } from '@material-ui/core'
import { AuthUtils } from '../../../redux/auth'
import { MODULES, QUERY_ACTIONS, QUERY_KEYS, ROLES } from '../../../config/constants'
import { useMutation } from '@tanstack/react-query'
import { removePanel, renamePanel, updateDashboardWidgets } from '../../../api'
import i18n from '../../../assets/i18n'
import routes from '../../../config/routes'
import {useHistory} from "react-router-dom";
import {manageQueriesWithPrefix} from "../../../api/mutations/mutationCallbacks";
import { ChartPie, CircleCheck, PencilLine, Save, Trash2 } from 'lucide-react'
import { tokens } from '../../../assets/tokens'
import Button from '@material-ui/core/Button'
import { removeQueriesByKeys } from '../../../api/mutations/mutationCallbacks'
const DashboardEdit = () => {

  const [dashboardTitle, setDashboardTitle] = useState(i18n.dashboard.edit.title);
  const [hasDashboardChanged, setHasDashboardChanged] = useState(false);
  const [originalWidgets, setOriginalWidgets] = useState([]);
  const [selectedWidgets, setSelectedWidgets] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [inputFilterValue, setInputFilterValue] = useState('');
  const [filteredWidgets, setFilteredWidgets] = useState([]);

  const [backendInProgress, setBackendInProgress] = useState(false);
  const [savingInProgress, setSavingInProgress] = useState(false);
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [newDashboardName, setNewDashboardName] = useState('');

  const [showAlert, setShowAlert] = useState(false);
  const [alert, setAlert] = useState({ message: '', severity: '' });

  const {id} = useParams()
  let {module} = useAppContext()
  module = module || MODULES.ID_TALENT_ACQUISITION;
  const history = useHistory();

  const redirectToHome = () => {
    history.push(routes.dashboardHome.replace(':module_id', module).replace(':id', ''));
  }

  const isAdminUser = AuthUtils.hasRole(ROLES.ROLE_ATS_ADMIN_talent_acquisition) || AuthUtils.hasRole(ROLES.ROLE_ATS_ADMIN_ROOT);
  if(!isAdminUser) {
    redirectToHome();
  }

  const {data: dashboardInfo, isLoading: isLoadingInfo } = useDashboardInfo(module, id)
  const {data: availableWidgets } = useAvailableWidgets(module)
  const {data: dashboardWidgets } = useDashboardWidgets(module, id)

  useEffect(() => {
    if(!isLoadingInfo) {
      if(!dashboardInfo || !dashboardInfo.is_custom) {
        redirectToHome();
      } else {
        setDashboardTitle(`${i18n.dashboard.edit.title} ${dashboardInfo.name}`)
        setNewDashboardName(dashboardInfo.name);
      }
    }
  }, [isLoadingInfo]);

  useEffect(() => {
    if(availableWidgets) {
      availableWidgets.map(widget => {
        widget.localized_name = i18n.dashboard.widgets[widget.name] || widget.name

        return widget;
      });

      setFilteredWidgets(availableWidgets || []);
    }
  }, [availableWidgets])

  useEffect(() => {
    if(dashboardWidgets) {
      setOriginalWidgets(() =>
        dashboardWidgets.map(data => {
          return {
            id: data.widget.id,
            params: data.params || {}
          }
        })
      );

      setSelectedWidgets(() =>
        dashboardWidgets.map(data => {
          return {
            id: data.widget.id,
            params: data.params || {}
          }
        })
      );
    }
  }, [dashboardWidgets])

  useEffect(() => {
    if(availableWidgets) {
      setFilteredWidgets(availableWidgets.filter(widget => {
        if(!selectedGroupId) {
          return true;
        }

        return widget.widget_group?.id === selectedGroupId;
      }));
    }
  }, [selectedGroupId])

  useEffect(() => {
    setHasDashboardChanged(!(JSON.stringify(originalWidgets) === JSON.stringify(selectedWidgets) || !selectedWidgets.length));
  }, [selectedWidgets, originalWidgets])


  const handleWidgetClick = (widgetId) => {
    setSelectedWidgets((prevWidgets) =>
      prevWidgets.find(item => item.id === widgetId)
        ? prevWidgets.filter((item) => item.id !== widgetId)
        : [...prevWidgets, { id: widgetId, params: {} }]
    );
  }

  const onFilterInputChange = (value) => {
    setInputFilterValue(value);

    if(!value || value.trim === '') {
      setFilteredWidgets(availableWidgets);
      return;
    }

    setSelectedGroupId(null);
    value = value.toLowerCase();

    setFilteredWidgets(availableWidgets.filter(widget => {
      return widget.localized_name?.toLowerCase().indexOf(value) > -1 || widget.description?.toLowerCase().indexOf(value) > -1
    }));
  }
  const onRenameInputChange = (value) => {
    setNewDashboardName(value)
    if(!value || value.trim === '') {
      return;
    }
  }

  const WidgetSidebar = ({ widgets = [], onGroupClick }) => {
    const groups = {};
    const totals = {};
    widgets.forEach(widget => {
      if(widget.widget_group) {
        if(!totals[widget.widget_group.keyword]) {
          totals[widget.widget_group.keyword] = 0;
        }

        totals[widget.widget_group.keyword]++;

        groups[widget.widget_group.keyword] = {
          total: totals[widget.widget_group.keyword],
          ...widget.widget_group
        };
      }
    });

    // Grupos de widgets: lista sobre tarjeta blanca, el activo con el tinte del color del cliente
    const renderGroup = (key, label, total, isActive, onClick) => (
      <button key={key} type="button" className={`widgets-group ${isActive ? 'active' : ''}`} onClick={onClick}>
        <span className="widgets-group-label">{label}</span>
        {total !== null && <span className="widgets-group-count">{total}</span>}
      </button>
    );

    return (
      <nav className="widgets-groups">
        {Object.values(groups).map(group => renderGroup(
          group.id,
          i18n.dashboard.widget_groups[group.keyword],
          group.total,
          selectedGroupId === group.id,
          () => onGroupClick(group.id)
        ))}
        <div className="widgets-groups-divider" />
        {renderGroup('all', i18n.dashboard.widget_groups.all, null, selectedGroupId === null, () => onGroupClick(null))}
      </nav>
    );
  };

  const handleGroupClick = (groupId) => {
    setSelectedGroupId((prevGroupId) => prevGroupId === groupId ? null : groupId);
    setInputFilterValue('');
  }

  const displayWidgetList = useMemo(() => {
    if(!filteredWidgets || filteredWidgets.length === 0) {
      return <></>;
    }


    return filteredWidgets.map((widget) => {
      if(!widget.widget_group) {
        return null
      }
      const isSelected = !!selectedWidgets.find(item => item.id === widget.id)
      return (
        <button
          key={widget.id}
          type="button"
          className={`widget-card ${isSelected ? 'selected' : ''}`}
          aria-pressed={isSelected}
          onClick={() => handleWidgetClick(widget.id)}
        >
          {isSelected && <CircleCheck size={18} className="widget-check" />}
          <span className="widget-image">{widget.preview_url && <img src={widget.preview_url} alt="" />}</span>
          <span className="widget-name">{widget.localized_name}</span>
        </button>
      )
    })
  }, [filteredWidgets, selectedWidgets]);

  const updateDashboardMutation = useMutation(async ({ widgets, module, id }) => {
      return await updateDashboardWidgets(widgets, module, id)
        .then(() => {
          displayAlert({ message: i18n.dashboard.edit.saved, severity: 'success' });
          setOriginalWidgets([...selectedWidgets]);

          setSavingInProgress(false);
          setBackendInProgress(false);

          manageQueriesWithPrefix([
            `${module}:${QUERY_KEYS.DASHBOARDS}:data:${id}`,
            `${module}:${QUERY_KEYS.DASHBOARDS}:widgets:${id}`
          ], QUERY_ACTIONS.REMOVE);
        });
    }
  );

  const displayAlert = (data, callback = null) => {
    setAlert(data);
    setShowAlert(true);
    setTimeout(() => {
      setShowAlert(false)

      callback && callback();
    }, 3500);
  }

  const handleSaveDashboard = () => {
    if(savingInProgress) {
      return;
    }

    setSavingInProgress(true);
    setBackendInProgress(true);

    updateDashboardMutation.mutate({
      widgets: selectedWidgets,
      module: module,
      id: id
    });
  }

  const handleRemoveDashboard = () => {
    if(backendInProgress) {
      return;
    }

    setBackendInProgress(true);
    removePanel(module, id).then((res) => {
      if (res && res.success) {
        removeQueriesByKeys( [`${module}:${QUERY_KEYS.DASHBOARDS}`]);
        setRemoveDialogOpen(false);

        displayAlert({message: i18n.dashboard.edit.removed, severity: "success"}, () => {
          history.push(routes.dashboardHome.replace(':module_id', module).replace(':id', ''))
        });
      } else {
        setBackendInProgress(false);
      }
    }).catch((e) => {
      // Si falla, el modal sigue abierto: solo se quita el spinner
      console.error(e)
      displayAlert({message: i18n.dashboard.edit.errorRemoving, severity: "error"});
      setBackendInProgress(false);
    });
  }

  const handleRenameDashboard = () => {
    if(backendInProgress) {
      return;
    }

    setBackendInProgress(true);
    renamePanel(module, id, newDashboardName).then((res) => {
      if (res && res.data && res.data.success) {
        removeQueriesByKeys( [`${module}:${QUERY_KEYS.DASHBOARDS}`]);
        dashboardInfo.name = newDashboardName;
        setDashboardTitle(`${i18n.dashboard.edit.title} ${newDashboardName}`)

        setRenameDialogOpen(false);
        setBackendInProgress(false);
        displayAlert({ message: i18n.dashboard.edit.renamed, severity: 'success' });
      } else {
        setBackendInProgress(false);
      }
    }).catch((e) => {
      // Si falla, el modal sigue abierto: solo se quita el spinner
      console.error(e)
      setBackendInProgress(false);
      displayAlert({message: i18n.dashboard.edit.errorRenaming, severity: "error"});
    });
  }

  const handleClickRemove = () => {
    setRemoveDialogOpen(true);
  }
  const handleCloseRemoveDialog = () => {
    setRemoveDialogOpen(false);
  }

  const handleClickRename = () => {
    setRenameDialogOpen(true);
  }
  const handleCloseRenameDialog = () => {
    setRenameDialogOpen(false);
  }

  return (
    <DashboardEditStyled>
      <div>
        <Snackbar
          className="alert-unsaved-changes"
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          message={i18n.dashboard.edit.alertHasUnsavedChanges}
          open={hasDashboardChanged}
          key="unsavedChanges"
        />

        <Snackbar
          open={showAlert}
          key="custom-alert"
          className={alert.severity}
          message={alert.message}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
        </Snackbar>
      </div>

      <Grid container className="title-container" justifyContent='space-between' alignItems='center'>
        <Grid item xs={12} sm={6} className="title">
          <PageTitle title={dashboardTitle} />
        </Grid>
        <div style={{display: "flex", gap: 16}}>
          <Button
            onClick={() => { history.push(routes.dashboardHome.replace(':module_id', module).replace(':id?', id)) }}
            variant="outlined"
            startIcon={<ChartPie size={18} />}
          >
            {i18n.dashboard.edit.viewDashboard}
          </Button>

          <Button
            onClick={handleClickRename}
            variant="outlined"
            startIcon={<PencilLine size={18} />}
          >
            {i18n.dashboard.edit.renameDashboard}
          </Button>
          <ActionDialog
            open={renameDialogOpen}
            title={i18n.dashboard.edit.renameDashboard}
            onClose={handleCloseRenameDialog}
            onConfirm={handleRenameDashboard}
            cancelLabel={i18n.dashboard.edit.cancel}
            confirmLabel={i18n.dashboard.edit.renameConfirm}
            confirmDisabled={dashboardInfo && dashboardInfo.name === newDashboardName}
            isPending={backendInProgress}
            disableBackdropClose
          >
            <InputField
              placeholder={i18n.dashboard.edit.placeholderRename}
              value={newDashboardName}
              handleOnChange={onRenameInputChange}
              key="rename-dashboard"
            />
          </ActionDialog>


          <Button
            onClick={handleClickRemove}
            variant="outlined"
            disabled={backendInProgress}
            startIcon={<Trash2 size={18} />}
            style={backendInProgress ? undefined : { color: tokens.color.error.main, borderColor: tokens.color.error.main }}
          >
            {i18n.dashboard.edit.disable}
          </Button>
          <ActionDialog
            open={removeDialogOpen}
            title={i18n.dashboard.edit.removeDialogTitle}
            onClose={handleCloseRemoveDialog}
            onConfirm={handleRemoveDashboard}
            cancelLabel={i18n.dashboard.edit.cancel}
            confirmLabel={i18n.dashboard.edit.removeConfirm}
            isPending={backendInProgress}
            confirmColor="error"
            maxWidth="xs"
          />

          <Button
            onClick={handleSaveDashboard}
            variant="contained"
            disabled={!hasDashboardChanged || backendInProgress}
            startIcon={<Save size={18} />}
            color={'primary'}
          >
            {savingInProgress ? i18n.dashboard.edit.saving : i18n.dashboard.edit.save}
          </Button>
        </div>
      </Grid>

      <div className="edit-layout">
        <aside className="edit-card widgets-sidebar">
          <WidgetSidebar widgets={availableWidgets} onGroupClick={handleGroupClick} />
        </aside>

        <section className="edit-card widgets-panel">
          <div className="widgets-panel-header">
            <h2>{i18n.dashboard.edit.addWidgets}</h2>
            <SearchInput
              placeholder={i18n.dashboard.edit.findWidgets}
              value={inputFilterValue}
              onChange={onFilterInputChange}
              width={280}
            />
          </div>

          <div className="widgets-list">
            {isLoadingInfo ? <SpinnerIcon/> : <div className="widgets-grid">{displayWidgetList}</div>}
          </div>
        </section>
      </div>
    </DashboardEditStyled>
  )
};


export default DashboardEdit
